import { PILLAR_ARTICLES } from "@/mocks/articlesMeta";
import { ARTICLES_EN } from "@/mocks/articlesEn";
import { ARTICLES_FR } from "@/mocks/articlesFr";
import { ARTICLES_DA } from "@/mocks/articlesDa";
import { ARTICLES_DE } from "@/mocks/articlesDe";

const ENV_SITE_URL = import.meta.env.VITE_SITE_URL as string | undefined;

/**
 * 站点真实域名，优先读取环境变量 VITE_SITE_URL，未配置时回退到已知的官方域名。
 */
export const SITE_URL = (ENV_SITE_URL || "https://allchinabuyspread.pro").replace(/\/+$/, "");

export const SITE_NAME = "AllChinaBuy Spreadsheet";

export const FAVICON =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png";

export interface SeoMeta {
  title: string;
  description: string;
  keywords: string;
  path: string;
  ogTitle?: string;
  ogDescription?: string;
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
}

const HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=Warm%20abstract%20product%20collage%20of%20folded%20clothing%20a%20pair%20of%20sneakers%20and%20a%20leather%20handbag%20arranged%20in%20a%20soft%20diagonal%20flow%20on%20a%20terracotta%20and%20warm%20cream%20gradient%20background%20stylized%20editorial%20still%20life%20with%20gentle%20shadows%20high%20detail&width=1600&height=900&seq=hero-home&orientation=landscape&nocache=true";

const GUIDE_IMAGE =
  "https://readdy.ai/api/search-image?query=Overhead%20view%20of%20a%20tidy%20desk%20with%20a%20printed%20product%20category%20checklist%20a%20pencil%20and%20folded%20clothing%20fabric%20swatches%20on%20a%20warm%20sage%20green%20and%20cream%20background%20editorial%20still%20life%20for%20a%20shopping%20guide%20high%20detail&width=1600&height=900&seq=hero-article&orientation=landscape&nocache=true";

const HOME_META: SeoMeta = {
  title: "AllChinaBuy Spreadsheet – Products, Sellers & Shipping Guides",
  description:
    "AllChinaBuy Spreadsheet is an independent resource hub for AllChinaBuy products, sellers, shipping and QC guides. Browse buying resources, reviews and product categories.",
  keywords:
    "AllChinaBuy Spreadsheet, AllChinaBuy review, AllChinaBuy shipping, AllChinaBuy guide, AllChinaBuy products",
  path: "/",
  ogTitle: "AllChinaBuy Spreadsheet – Products, Sellers & Shipping Guides",
  ogDescription:
    "An independent resource hub for AllChinaBuy products, sellers, shipping and QC guides.",
  image: HERO_IMAGE,
  type: "website",
};

const GUIDE_META: SeoMeta = {
  title: "AllChinaBuy Spreadsheet: The Complete Beginner Guide",
  description:
    "Learn what the AllChinaBuy Spreadsheet is, how to use it to find products, sellers and shipping options, and the steps to complete your first AllChinaBuy order with confidence.",
  keywords:
    "AllChinaBuy Spreadsheet guide, AllChinaBuy buying guide, AllChinaBuy beginner guide, AllChinaBuy products, AllChinaBuy shipping",
  path: "/spreadsheet-guide",
  image: GUIDE_IMAGE,
  type: "article",
};

interface ArticleSummary {
  title: string;
  excerpt: string;
}

const CONTENT_MAPS: Record<string, Record<string, ArticleSummary>> = {
  en: ARTICLES_EN as unknown as Record<string, ArticleSummary>,
  fr: ARTICLES_FR as unknown as Record<string, ArticleSummary>,
  da: ARTICLES_DA as unknown as Record<string, ArticleSummary>,
  de: ARTICLES_DE as unknown as Record<string, ArticleSummary>,
};

function pickLang(language: string): string {
  if (language?.startsWith("fr")) return "fr";
  if (language?.startsWith("da")) return "da";
  if (language?.startsWith("de")) return "de";
  return "en";
}

export function resolveSeo(pathname: string, language: string): SeoMeta {
  if (pathname === "/") {
    return HOME_META;
  }

  if (pathname === "/spreadsheet-guide") {
    return GUIDE_META;
  }

  const articleMatch = pathname.match(/^\/article\/([^/]+)$/);
  if (articleMatch) {
    const slug = articleMatch[1];
    const meta = PILLAR_ARTICLES.find((a) => a.slug === slug);
    if (meta) {
      const lang = pickLang(language);
      const content = CONTENT_MAPS[lang]?.[slug] ?? CONTENT_MAPS.en[slug];
      const baseTitle = content?.title ?? SITE_NAME;
      return {
        title: `${baseTitle} | ${SITE_NAME}`,
        description:
          content?.excerpt ?? `Read our original guide about ${baseTitle} for AllChinaBuy.`,
        keywords: `AllChinaBuy Spreadsheet, AllChinaBuy ${meta.slug}, AllChinaBuy guide, AllChinaBuy products`,
        path: `/article/${slug}`,
        image: meta.image,
        type: "article",
      };
    }
  }

  return {
    title: "Page Not Found | AllChinaBuy Spreadsheet",
    description: "The page you are looking for could not be found.",
    keywords: "AllChinaBuy Spreadsheet",
    path: pathname,
    noindex: true,
  };
}

export function resolveCanonical(pathname: string): string {
  return `${SITE_URL}${pathname === "/" ? "/" : pathname}`;
}